import { z } from 'zod'

import { MAX_PRICE, PriceSchema } from './shop'

// Online store, phase 3: discount codes. The CMS sets them up; the shop only ever sends the code a visitor typed. The
// server checks it and works out the discount in the cart quote and again when the order is placed. Every code has a
// concrete total cap and a per-customer cap: an empty field gets the default below, never "no limit".

export const PROMO_KINDS = ['percent', 'fixed', 'free_shipping'] as const
export const PromoKindSchema = z.enum(PROMO_KINDS)
export type PromoKind = z.infer<typeof PromoKindSchema>

export const PROMO_LIMITS = {
  /** Codes one shop can have. */
  codes: 50,
  /** Uses of one code across all customers when the field is left empty, and the highest number accepted. */
  usageDefault: 100,
  usageMax: 10_000,
  /** Uses per customer (by email) when left empty, and the highest number accepted. */
  perCustomerDefault: 1,
  perCustomerMax: 20,
  percentMax: 90,
} as const

/** What a visitor types: letters and digits, any case, spaces around ignored. */
export const PromoCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{3,20}$/, 'promo_code')

const DaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')

/** The CMS form. Dates are Jakarta days, both inclusive; empty means "from now" / "no end date". */
export const PromoInputSchema = z
  .object({
    code: PromoCodeSchema,
    kind: PromoKindSchema,
    /** Percent (1–90), whole rupiah off, or 0 for free shipping. */
    value: z.number().int().min(0).max(MAX_PRICE),
    /** Subtotal the cart must reach (before the discount), whole rupiah. */
    minOrder: PriceSchema.default(0),
    startsOn: DaySchema.nullable().default(null),
    endsOn: DaySchema.nullable().default(null),
    usageLimit: z.number().int().min(1).max(PROMO_LIMITS.usageMax).nullish().transform((v) => v ?? PROMO_LIMITS.usageDefault),
    perCustomerLimit: z.number().int().min(1).max(PROMO_LIMITS.perCustomerMax).nullish().transform((v) => v ?? PROMO_LIMITS.perCustomerDefault),
    active: z.boolean().default(true),
    /** Shown to shoppers as a hint in the cart ("Try HEMAT10"). */
    showHint: z.boolean().default(false),
    /** For the team only. */
    note: z.string().trim().max(200).default(''),
  })
  .superRefine((p, ctx) => {
    if (p.kind === 'percent' && (p.value < 1 || p.value > PROMO_LIMITS.percentMax)) ctx.addIssue({ code: 'custom', path: ['value'], message: `Between 1 and ${PROMO_LIMITS.percentMax} percent` })
    if (p.kind === 'fixed' && p.value < 1) ctx.addIssue({ code: 'custom', path: ['value'], message: 'Enter the amount off' })
    if (p.kind === 'free_shipping' && p.value !== 0) ctx.addIssue({ code: 'custom', path: ['value'], message: 'Free shipping has no amount' })
    if (p.startsOn && p.endsOn && p.endsOn < p.startsOn) ctx.addIssue({ code: 'custom', path: ['endsOn'], message: 'The end date is before the start date' })
  })
export type PromoInput = z.input<typeof PromoInputSchema>
export type PromoValues = z.output<typeof PromoInputSchema>

export const PromoSchema = z.object({
  id: z.string(),
  code: z.string(),
  kind: PromoKindSchema,
  value: z.number().int(),
  minOrder: z.number().int(),
  startsOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  usageLimit: z.number().int(),
  perCustomerLimit: z.number().int(),
  active: z.boolean(),
  showHint: z.boolean(),
  note: z.string(),
  /** Uses held by orders that are not expired or cancelled. */
  used: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Promo = z.infer<typeof PromoSchema>
export const PromoListSchema = z.object({ items: z.array(PromoSchema) })

// ── Checking a code ──────────────────────────────────────────────────────
export const PROMO_REFUSALS = ['unknown', 'not_started', 'ended', 'min_order', 'used_up', 'customer_limit'] as const
export const PromoRefusalSchema = z.enum(PROMO_REFUSALS)
export type PromoRefusal = z.infer<typeof PromoRefusalSchema>

/** Today in Jakarta (UTC+7, no daylight saving) as YYYY-MM-DD. */
export const wibDay = (now: Date = new Date()) => new Date(now.getTime() + 7 * 3_600_000).toISOString().slice(0, 10)

type PromoRule = Pick<PromoValues, 'kind' | 'value' | 'minOrder' | 'startsOn' | 'endsOn' | 'usageLimit' | 'perCustomerLimit' | 'active'>

/**
 * Whether a code can be used now: active, inside its dates (Jakarta days), the cart reaches the minimum, and uses
 * are left. `usedByCustomer` is unknown in the cart (no email yet) and checked when the order is placed.
 */
export function promoRefusal(p: PromoRule, at: { subtotal: number; today: string; used: number; usedByCustomer?: number | undefined }): PromoRefusal | null {
  if (!p.active) return 'unknown'
  if (p.startsOn && at.today < p.startsOn) return 'not_started'
  if (p.endsOn && at.today > p.endsOn) return 'ended'
  if (at.used >= p.usageLimit) return 'used_up'
  if (at.usedByCustomer !== undefined && at.usedByCustomer >= p.perCustomerLimit) return 'customer_limit'
  if (at.subtotal < p.minOrder) return 'min_order'
  return null
}

/** Rupiah off the goods (not shipping). Percent rounds down; a fixed amount never exceeds the subtotal. */
export function promoItemsDiscount(p: Pick<PromoValues, 'kind' | 'value'>, subtotal: number): number {
  if (p.kind === 'percent') return Math.floor((subtotal * p.value) / 100)
  if (p.kind === 'fixed') return Math.min(p.value, subtotal)
  return 0
}

/** An order's discount and total. Free shipping takes the whole shipping price off. */
export function orderTotals(subtotal: number, shippingPrice: number, promo: { discount: number; freeShipping: boolean } | null): { discount: number; total: number } {
  const discount = promo ? promo.discount + (promo.freeShipping ? shippingPrice : 0) : 0
  return { discount, total: subtotal + shippingPrice - discount }
}

/** The code as the quote answers it: applied (with its discount) or refused (with why). */
export const PromoQuoteSchema = z.object({
  code: z.string(),
  status: z.union([z.literal('applied'), PromoRefusalSchema]),
  kind: PromoKindSchema.nullable(),
  value: z.number().int(),
  minOrder: z.number().int(),
  /** Off the goods; 0 unless applied. */
  discount: z.number().int(),
  /** Shipping becomes free at checkout (applied free-shipping codes only). */
  freeShipping: z.boolean(),
})
export type PromoQuote = z.infer<typeof PromoQuoteSchema>

/** A code the shop suggests in the cart (CMS: "Show as a hint"). */
export const PromoHintSchema = z.object({ code: z.string(), kind: PromoKindSchema, value: z.number().int(), minOrder: z.number().int() })
export type PromoHint = z.infer<typeof PromoHintSchema>

/** At most this many hints are shown. */
export const PROMO_HINTS = 3
